import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Paper, Stack, Switch, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  MyNotificationPreferenceCatalogQuery,
  NotificationPreferenceAudience,
  NotificationPreferenceScope,
} from "generated-graphql/graphql";
import { useState } from "react";

import {
  MY_NOTIFICATION_PREFERENCE_CATALOG,
  SET_STAFF_NOTIFICATION_PAUSES,
} from "./notificationPreferenceDocuments";
import { PreferenceCheckbox } from "./PreferenceCheckbox";
import {
  CHANNEL_CELL_SX,
  PAUSED_FOR_ALL_STAFF_COPY,
  ROLLUP_DESCRIPTION,
  rollupLabel,
  SECTION_HEADING_SX,
  WIDGET_SX,
} from "./preferenceProvenance";
import { useExpandedSections } from "./preferencesPageState";
import { LoadingState } from "./PreferencesPanel";
import { SectionAccordion, SectionHeaderStatus } from "./SectionAccordion";

type CatalogSection =
  MyNotificationPreferenceCatalogQuery["myNotificationPreferenceCatalog"]["sections"][number];

const PANEL_MAX_WIDTH = 840;

/** Every section, as a staff member's own settings for every organization list them. */
const CATALOG_VARIABLES = {
  tenantId: null,
  scope: NotificationPreferenceScope.My,
  audience: NotificationPreferenceAudience.Staff,
};

/**
 * The rows' grid: the alert, its one control (as wide as a channel column on My
 * preferences), and the section header's caret lane.
 */
const PAUSE_GRID_COLUMNS = "minmax(0, 1fr) 76px 40px";

const CONTROL_ROW_SX = {
  display: "grid",
  gridTemplateColumns: PAUSE_GRID_COLUMNS,
  alignItems: "center",
  columnGap: 1,
  py: 1,
} as const;

/** Beside a section's name: how much of it is paused, or nothing when none of it is. */
function sectionPauseStatus(pausedCount: number, alertCount: number): string | null {
  if (pausedCount === 0) {
    return null;
  }
  return pausedCount === alertCount ? "Paused" : `${pausedCount} of ${alertCount} paused`;
}

/** How much of a section is paused, for its checkbox's tooltip and description. */
function sectionPauseExplanation(pausedCount: number, alertCount: number): string {
  if (pausedCount === 0) {
    return "On for all staff";
  }
  if (pausedCount === alertCount) {
    return PAUSED_FOR_ALL_STAFF_COPY;
  }
  return `${pausedCount} of ${alertCount} paused for all staff`;
}

/**
 * One collapsible section: a line whose checkbox turns every alert in it on or off,
 * mixed while only some are paused, then a switch per alert. While an alert saves, only
 * the controls that cover it are off.
 */
function PauseSection({
  section,
  savingKinds,
  onChange,
  expanded,
  onToggleExpanded,
}: {
  section: CatalogSection;
  savingKinds: ReadonlySet<string>;
  onChange: (kinds: string[], enabled: boolean) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const pausedCount = section.rows.filter((row) => row.pausedForAllStaff).length;
  const sectionRollupLabel = rollupLabel(section.label);
  const status = sectionPauseStatus(pausedCount, section.rows.length);

  return (
    <SectionAccordion
      label={section.label}
      status={status && <SectionHeaderStatus>{status}</SectionHeaderStatus>}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      gridTemplateColumns={PAUSE_GRID_COLUMNS}
      controlColumns={1}
    >
      {/* A heavier rule than between alerts, as on My preferences: it governs the list. */}
      <Box sx={{ ...CONTROL_ROW_SX, borderBottom: 2, borderColor: "divider" }}>
        <Box>
          <Typography variant="body1" fontWeight={500}>
            {sectionRollupLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ROLLUP_DESCRIPTION}
          </Typography>
        </Box>
        <Box sx={CHANNEL_CELL_SX}>
          <PreferenceCheckbox
            label={`${sectionRollupLabel} for all staff`}
            explanation={sectionPauseExplanation(pausedCount, section.rows.length)}
            // Checked only when nothing is paused, so a click from mixed resumes the rest.
            checked={pausedCount === 0}
            indeterminate={pausedCount > 0 && pausedCount < section.rows.length}
            disabled={section.rows.some((row) => savingKinds.has(row.kind))}
            onChange={(enabled) =>
              onChange(
                section.rows.map((row) => row.kind),
                enabled
              )
            }
          />
        </Box>
        {/* The header's caret lane, reserved so the columns line up. */}
        <Box />
      </Box>
      {section.rows.map((row) => (
        <Box
          key={row.kind}
          sx={{
            ...CONTROL_ROW_SX,
            borderBottom: 1,
            borderColor: "divider",
            "&:last-of-type": { borderBottom: 0 },
          }}
        >
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {row.title}
            </Typography>
            {row.description && (
              <Typography variant="body2" color="text.secondary">
                {row.description}
              </Typography>
            )}
          </Box>
          <Box sx={CHANNEL_CELL_SX}>
            <Switch
              size="small"
              checked={!row.pausedForAllStaff}
              disabled={savingKinds.has(row.kind)}
              onChange={(event) => onChange([row.kind], event.target.checked)}
              slotProps={{
                input: { role: "switch", "aria-label": `${row.title} for all staff` },
              }}
            />
          </Box>
          <Box />
        </Box>
      ))}
    </SectionAccordion>
  );
}

/**
 * The All staff tab: one switch per alert, where off pauses it for every staff member,
 * in-app and email, whatever their own settings say. Customers still get it.
 */
export function AllStaffTab() {
  const alerts = useAlerts();
  const catalogQuery = useQuery(MY_NOTIFICATION_PREFERENCE_CATALOG, {
    variables: CATALOG_VARIABLES,
    fetchPolicy: "cache-and-network",
  });
  const [setPauses] = useMutation(SET_STAFF_NOTIFICATION_PAUSES, {
    onError: (error) => alerts.error(error.message),
    refetchQueries: [MY_NOTIFICATION_PREFERENCE_CATALOG],
    awaitRefetchQueries: true,
  });
  // The alerts with a save in flight. A kind is never in two at once: its switches are off
  // until its save settles.
  const [savingKinds, setSavingKinds] = useState<ReadonlySet<string>>(new Set());
  const { isExpanded, toggle } = useExpandedSections("notification-all-staff-collapsed-sections");
  const catalog = catalogQuery.data?.myNotificationPreferenceCatalog;

  const handleChange = async (kinds: string[], enabled: boolean) => {
    setSavingKinds((current) => new Set([...current, ...kinds]));
    try {
      await setPauses({ variables: { kinds, paused: !enabled } });
    } finally {
      setSavingKinds((current) => new Set([...current].filter((kind) => !kinds.includes(kind))));
    }
  };

  return (
    <Box sx={{ maxWidth: PANEL_MAX_WIDTH }}>
      <Paper elevation={0} sx={WIDGET_SX}>
        <Typography variant="overline" color="text.secondary" sx={SECTION_HEADING_SX}>
          Notifications for all staff
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Turning an alert off pauses it for every Encamp staff member, in-app and email, whatever
          their own settings say. Customers still get it.
        </Typography>
        {catalogQuery.error && (
          <Alert severity="error">
            Failed to load the All staff settings: {catalogQuery.error.message}
          </Alert>
        )}
        {!catalogQuery.error && !catalog && <LoadingState />}
        {/* No spacing between sections: they are bands inside one widget, as on My preferences. */}
        <Stack>
          {catalog?.sections.map((section) => (
            <PauseSection
              key={section.groupId}
              section={section}
              savingKinds={savingKinds}
              onChange={(kinds, enabled) => void handleChange(kinds, enabled)}
              expanded={isExpanded(section.groupId)}
              onToggleExpanded={() => toggle(section.groupId)}
            />
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
